import React, { useState, createContext, useCallback, useMemo } from 'react';
import { API } from 'aws-amplify';
import {
  SimPilotInterface,
  SimSaveData,
  SimRequestRetrieveData,
  simSubmitData
} from '../models/SimArtPilotInterface';

type SimArtPilotContextValue = {
  saveSimRequest: (data: SimSaveData) => Promise<SimPilotInterface>;
  submitSimRequest: (data: simSubmitData) => Promise<SimPilotInterface>;
  simRetrieveRequests: (data?: any) => Promise<SimRequestRetrieveData[]>;
  deleteSimRequest: (data?: any) => Promise<SimRequestRetrieveData[]>;
  loading: boolean;
  retrieveData: SimRequestRetrieveData[];
  refresh: (params?: any) => Promise<SimRequestRetrieveData[]>;
  submitData: string;
  artRetrieveRequests: (data?: any) => Promise<any[]>;
  saveArtRequest: (data: any) => Promise<any>;
  deleteArtRequest: (data: {
    artRequestId: number;
    artYear: number;
    artMonth: number;
    action: string;
  }) => Promise<any>;
  submitArtRequest: (data: any) => Promise<any>;
  artLoading: boolean;
  artRetrieveData: any[];
  artSubmitData: string;
  artBaseLocations: any[];
};

const simArtPilotContext = createContext<SimArtPilotContextValue>({
  saveSimRequest: (data: SimSaveData): Promise<SimPilotInterface> => {
    return Promise.resolve({} as SimPilotInterface);
  },
  submitSimRequest: (data: simSubmitData): Promise<SimPilotInterface> => {
    return Promise.resolve({} as SimPilotInterface);
  },
  simRetrieveRequests: (data: any): Promise<SimRequestRetrieveData[]> => {
    return Promise.resolve([]);
  },
  deleteSimRequest: (data: {
    simRequestId: string;
    simYear: number;
    simMonth: number;
    action: string;
  }): Promise<any> => {
    return Promise.resolve();
  },
  loading: false,
  retrieveData: [],
  refresh: async () => [],
  submitData: '',
  artSubmitData: '',
  artRetrieveRequests: (data: any): Promise<any[]> => {
    return Promise.resolve([]);
  },
  saveArtRequest: (data: any): Promise<any> => {
    return Promise.resolve({});
  },
  deleteArtRequest: (data: {
    artRequestId: number;
    artYear: number;
    artMonth: number;
    action: string;
  }): Promise<any> => {
    return Promise.resolve({});
  },
  submitArtRequest: (data: any): Promise<any> => {
    return Promise.resolve({});
  },
  artLoading: false,
  artRetrieveData: [],
  artBaseLocations: []
});

export const SimArtPilotProvider = (props: React.PropsWithChildren<{}>) => {
  const [loading, setLoading] = useState(false);
  const [retrieveData, setRetrieveData] = useState<SimRequestRetrieveData[]>(
    []
  );
  const [submitData, setSubmitData] = useState<string>('');
  const [artSubmitData, setArtSubmitData] = useState<string>('');
  const [artLoading, setArtLoading] = useState(false);
  const [artRetrieveData, setArtRetrieveData] = useState<any[]>([]);
  const [artBaseLocations, setArtBaseLocations] = useState<any[]>([]);

  const simRetrieveRequests = useCallback(
    async (data?: any): Promise<SimRequestRetrieveData[]> => {
      setLoading(true);
      try {
        const requestObject = { body: data };

        const response = await API.post(
          'crewlife',
          '/sim/request/retrieve',
          requestObject
        );

        // Handle the retrieveData as before
        if (Array.isArray(response.requests)) {
          setRetrieveData(response.requests);
          setSubmitData(response.submittedOn);
          return response.retrieveData;
        } else if (Array.isArray(response)) {
          setRetrieveData(response);
          return response;
        } else {
          setRetrieveData([]);
          return [];
        }
      } catch (error) {
        console.error('Error fetching SIM settings:', error);
        setRetrieveData([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const submitSimRequest = useCallback(
    async (data: simSubmitData): Promise<SimPilotInterface> => {
      setLoading(true);
      const requestObject = { body: data };
      try {
        const response = await API.post(
          'crewlife',
          '/sim/request/submit',
          requestObject
        );
        // You may want to handle the response here, e.g. return response as SimPilotInterface
        return response as SimPilotInterface;
      } catch (error: any) {
        console.error('Error submitting SIM request:', error, error.response);
        throw error.response;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const saveSimRequest = useCallback(
    async (data: SimSaveData): Promise<SimPilotInterface> => {
      setLoading(true);
      const requestObject = {
        body: data
      };
      try {
        const response = await API.post(
          'crewlife',
          '/sim/request/save',
          requestObject
        );

        if (response && response.statusCode && response.statusCode === 200) {
          try {
            await simRetrieveRequests({
              simYear: data.simYear,
              simMonth: data.simMonth
            });
          } catch (error) {
            console.error('Error fetching SIM settings after save:', error);
          }

          return response;
        } else {
          // Return an empty SimPilotInterface if response is not as expected
          console.error('Unexpected response:', response);
          const errorMessage =
            response?.message ||
            response?.error ||
            'Save request failed with unexpected response';
          throw new Error(errorMessage);
        }
      } catch (error: any) {
        console.error('Error fetching SIM settings:', error.response);
        console.error('Unexpected response:', error);
        throw error.response;
      } finally {
        setLoading(false);
      }
    },
    [simRetrieveRequests]
  );

  const deleteSimRequest = useCallback(
    async (data: {
      deleteSimRequest: string;
      simYear: number;
      simMonth: number;
    }) => {
      setLoading(true);
      const requestObject = { body: data };
      console.log('deleteSimRequest requestObject', requestObject);
      try {
        const response = await API.post(
          'crewlife',
          '/sim/request/save',
          requestObject
        );

        if (response && response.statusCode && response.statusCode === 200) {
          try {
            await simRetrieveRequests({
              simYear: data.simYear,
              simMonth: data.simMonth
            });
          } catch (error) {
            console.error('Error fetching SIM settings after delete:', error);
          }

          return response;
        } else {
          // Return an empty SimPilotInterface if response is not as expected
          console.error('Unexpected response:', response);
          const errorMessage =
            response?.message ||
            response?.error ||
            'Delete request failed with unexpected response';
          throw new Error(errorMessage);
        }
      } catch (error: any) {
        console.error('Error deleting SIM request:', error.response);
        console.error('Unexpected response:', error);
        throw error.response;
      } finally {
        setLoading(false);
      }
    },
    [simRetrieveRequests]
  );

  // ART-specific API integration (kept separate so SIM flow isn't affected)
  const artRetrieveRequests = useCallback(
    async (data?: any): Promise<any[]> => {
      setArtLoading(true);
      try {
        const requestObject = { body: data };
        const response = await API.post(
          'crewlife',
          '/art/request/retrieve',
          requestObject
        );

        console.log('ART API Response:', response);
        console.log('Base Locations in response:', response.baseLocations);

        // Temporary fallback for testing if API doesn't return baseLocations
        const fallbackBaseLocations = [
          { code: 'YUL', label: 'Montréal - YUL' },
          { code: 'YYZ', label: 'Toronto - YYZ' },
          { code: 'YWG', label: 'Winnipeg - YWG' },
          { code: 'YVR', label: 'Vancouver - YVR' }
        ];

        if (Array.isArray(response.requests)) {
          setArtRetrieveData(response.requests);
          setArtSubmitData(response.submittedOn);
          setArtBaseLocations(response.baseLocations || fallbackBaseLocations);

          return response.requests;
        } else if (Array.isArray(response)) {
          setArtRetrieveData(response);
          setArtBaseLocations(fallbackBaseLocations);

          return response;
        } else {
          setArtRetrieveData([]);
          setArtSubmitData('');
          setArtBaseLocations(fallbackBaseLocations);

          return [];
        }
      } catch (error) {
        console.error('Error fetching ART requests:', error);
        setArtRetrieveData([]);
        setArtBaseLocations([]);
        throw error;
      } finally {
        setArtLoading(false);
      }
    },
    []
  );

  const saveArtRequest = useCallback(
    async (data: any): Promise<any> => {
      setArtLoading(true);
      const requestObject = { body: data };
      try {
        const response = await API.post(
          'crewlife',
          '/art/request/save',
          requestObject
        );

        if (response && response.statusCode && response.statusCode === 200) {
          try {
            await artRetrieveRequests({
              artYear: data.artYear,
              artMonth: data.artMonth
            });
          } catch (error) {
            console.error('Error fetching ART settings after save:', error);
          }

          return response;
        } else {
          console.error('Unexpected ART save response:', response);
          const errorMessage =
            response?.message ||
            response?.error ||
            'ART save failed with unexpected response';
          throw new Error(errorMessage);
        }
      } catch (error: any) {
        console.error('Error saving ART request:', error);
        throw error?.response || error;
      } finally {
        setArtLoading(false);
      }
    },
    [artRetrieveRequests]
  );

  const deleteArtRequest = useCallback(
    async (data: {
      artRequestId: number;
      artYear: number;
      artMonth: number;
      action: string;
    }) => {
      setArtLoading(true);
      const requestObject = { body: data };
      try {
        const response = await API.post(
          'crewlife',
          '/art/request/save',
          requestObject
        );

        if (response && response.statusCode && response.statusCode === 200) {
          try {
            await artRetrieveRequests({
              artYear: data.artYear,
              artMonth: data.artMonth
            });
          } catch (error) {
            console.error('Error fetching ART settings after delete:', error);
          }

          return response;
        } else {
          console.error('Unexpected ART delete response:', response);
          const errorMessage =
            response?.message ||
            response?.error ||
            'ART delete failed with unexpected response';
          throw new Error(errorMessage);
        }
      } catch (error: any) {
        console.error('Error deleting ART request:', error);
        throw error?.response || error;
      } finally {
        setArtLoading(false);
      }
    },
    [artRetrieveRequests]
  );

  const submitArtRequest = useCallback(async (data: any): Promise<any> => {
    setArtLoading(true);
    const requestObject = { body: data };
    try {
      const response = await API.post(
        'crewlife',
        '/art/request/submit',
        requestObject
      );
      return response;
    } catch (error: any) {
      console.error('Error submitting ART request:', error);
      throw error?.response || error;
    } finally {
      setArtLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      saveSimRequest,
      simRetrieveRequests,
      submitSimRequest,
      deleteSimRequest,
      loading,
      retrieveData,
      refresh: simRetrieveRequests,
      submitData,
      // ART methods
      artRetrieveRequests,
      saveArtRequest,
      deleteArtRequest,
      submitArtRequest,
      artLoading,
      artRetrieveData,
      artSubmitData,
      artBaseLocations
    }),
    [
      saveSimRequest,
      simRetrieveRequests,
      submitSimRequest,
      deleteSimRequest,
      loading,
      retrieveData,
      submitData,
      artRetrieveRequests,
      saveArtRequest,
      deleteArtRequest,
      submitArtRequest,
      artLoading,
      artRetrieveData,
      artSubmitData,
      artBaseLocations
    ]
  );

  return (
    <simArtPilotContext.Provider value={value}>
      {props.children}
    </simArtPilotContext.Provider>
  );
};

export const useSimArtPilot = () => {
  return React.useContext(simArtPilotContext);
};
